import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import _ from "lodash";
import classnames from "classnames";
import { capitalize } from "@material-ui/core";
// UI components
import Box from '@material-ui/core/Box';
import Typography from "../../../components/Typography/Typography";
import Button from "../../../components/Button/Button";
import TextField from "../../../components/TextField/TextField";
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Modal from '../../../components/Modal/Modal.js';
import Multiselect from '../../../components/Multiselect/Multiselect';
// Hooks
import { useTranslation } from 'react-i18next';
import { useStyles } from "./style";
//Assets
import CloseIcon from '../../../assets/icons/CloseIcon.svg';
import { ReactComponent as EyeIcon } from '../../../assets/icons/EyeIconNew.svg';
import { ReactComponent as EyeIconActive } from '../../../assets/icons/EyeIconNewActive.svg';
import { ReactComponent as ArrowDown } from "../../../assets/icons/ArrowDownGrey.svg";
//Redux
import { createAdminUserRequest } from "../../../store/adminusers/requests";
import { createAdminUserStatusSelector, createAdminUserFailureMessageSelector } from "../../../store/adminusers/selectors";
import { actions as adminusersActions } from "../../../store/adminusers/slice";
// Constants
import { REQUEST_SUCCESS, REQUEST_FAILURE } from "../../../constants/statuses";

const roleOptions = [
    {
        name: "Super admin",
        type: "super_admin"
    },
    {
        name: "Admin",
        type: "admin"
    },
    {
        name: "Editor",
        type: "editor"
    },
    {
        name: "Moderator",
        type: "moderator"
    }
]

const AddNewUserModal = ({
    dialogOpened,
    onClose
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const createAdminUserStatus = useSelector(createAdminUserStatusSelector);
    const createAdminUserFailureMessage = useSelector(createAdminUserFailureMessageSelector);

    const [emailValue, setEmailValue] = useState("");
    const [nameValue, setNameValue] = useState("");
    const [passwordValue, setPasswordValue] = useState("");
    const [confirmPasswordValue, setConfirmPasswordValue] = useState("");
    const [roleValue, setRoleValue] = useState("Super admin");

    const create = () => {
        dispatch(createAdminUserRequest({ data: { name: nameValue, email: emailValue, password: passwordValue, "password_confirmation": confirmPasswordValue, role: roleOptions.find(card => card.name === roleValue).type} }));
    };

    useEffect(() => {
        createAdminUserStatus === REQUEST_SUCCESS && handleClose();
    }, [createAdminUserStatus])

    const handleClose = () => {
        onClose();
        setEmailValue("");
        setNameValue("");
        setPasswordValue("");
        setConfirmPasswordValue("");
        setRoleValue("Super admin");
        setPasswordFieldType(true);
        setPasswordConfirmationFieldType(true);
        dispatch(adminusersActions.clearCreateAdminUserFailureMessage());
    };

    const handlePasswordChange = (event) => {
        setPasswordValue(event.target.value);
    }

    const handleConfirmPasswordChange = (event) => {
        setConfirmPasswordValue(event.target.value);
    }

    const handleEmailChange = (event) => {
        setEmailValue(event.target.value);
    }

    const handleRoleChange = (event) => {
        setRoleValue(event.target.value);
    }

    const handleNameChange = (event) => {
        setNameValue(event.target.value);
    }

    const handlePasswordFieldTypeChange = () => {
        setPasswordFieldType(prevPasswordFieldType => !prevPasswordFieldType);
    };
    const [passwordFieldType, setPasswordFieldType] = useState(true);

    const handlePasswordConfirmationFieldTypeChange = () => {
        setPasswordConfirmationFieldType(prevPasswordFieldType => !prevPasswordFieldType);
    };
    const [passwordConfirmationFieldType, setPasswordConfirmationFieldType] = useState(true);

    const validateEmail = email => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    return (
        <Modal
            onClose={handleClose}
            dialogOpened={dialogOpened}
            closeButtonIcon={CloseIcon}
            paddingSpace="32px"
            borderRadius="16px"
        >
            <MuiDialogTitle disableTypography className={classes.dialogTitleWrapper}>
                <Typography variant="paragraph" component="div" className={classes.filterDialogTitle}>{t('users.addNewUserTitle')}</Typography>
            </MuiDialogTitle>
            <Box className={classes.rows}>
                <div className={classes.row}>
                    <TextField testId="header-name-field" value={nameValue || ""} className={classes.modalInput} fullWidth label={t("users.nameInputPlaceholder")} onChange={handleNameChange}></TextField>
                </div>
                <div className={classes.row}>
                    <TextField
                        testId="header-email-field"
                        autocomplete="one-time-code"
                        value={emailValue || ""}
                        className={classes.modalInput}
                        fullWidth
                        label={t("users.emailInputPlaceholder")}
                        onChange={handleEmailChange}
                        error={!!emailValue && !validateEmail(emailValue)}
                        {...!!emailValue && !validateEmail(emailValue) && { helperText: t("common.emailError") }}
                    />
                </div>
                <div className={classes.row}>
                    <TextField
                        testId="header-password-field"
                        autocomplete="one-time-code"
                        value={passwordValue || ""}
                        className={classes.modalInput}
                        type={passwordFieldType ? "password" : "text"}
                        fullWidth
                        label={t("users.passwordInputPlaceholder")}
                        onChange={handlePasswordChange}
                        error={!!passwordValue && passwordValue.length < 8}
                        {...(!!passwordValue && passwordValue.length < 8) && { helperText: t("common.passwordError") }}
                        endAdornment={passwordFieldType ? <EyeIcon className={classes.eye} onClick={handlePasswordFieldTypeChange} /> : <EyeIconActive className={classes.eye} onClick={handlePasswordFieldTypeChange} />}
                    />
                </div>
                <div className={classes.row}>
                    <TextField
                        testId="header-confirm-password-field"
                        value={confirmPasswordValue || ""}
                        autocomplete="one-time-code"
                        className={classes.modalInput}
                        fullWidth
                        type={passwordConfirmationFieldType ? "password" : "text"}
                        label={t("users.confirmPasswordInputPlaceholder")}
                        onChange={handleConfirmPasswordChange}
                        error={!!confirmPasswordValue && passwordValue !== confirmPasswordValue}
                        {...(!!confirmPasswordValue && passwordValue !== confirmPasswordValue) && { helperText: t("common.passwordConfirmError") }}
                        endAdornment={passwordConfirmationFieldType ? <EyeIcon className={classes.eye} onClick={handlePasswordConfirmationFieldTypeChange} /> : <EyeIconActive className={classes.eye} onClick={handlePasswordConfirmationFieldTypeChange} />}
                    />
                </div>
                <div className={classes.row}>
                    <Multiselect
                        testId="multiselect-role"
                        className={classes.rowWrap}
                        customClass={classes.multiselect}
                        labelClass={classes.labelClass}
                        iconArrow={<ArrowDown className={classes.arrowDown} />}
                        label={t('users.roleInputPlaceholder')}
                        options={roleOptions}
                        value={capitalize(roleValue)}
                        placeholder={roleOptions[0].name}
                        onChange={handleRoleChange}
                    />
                    {/* <TextField testId="header-role-field" value={roleValue || ""} className={classes.modalInput} fullWidth label={t("users.roleInputPlaceholder")} onChange={handleRoleChange}></TextField> */}
                </div>
            </Box>
            {!!createAdminUserFailureMessage && <Typography variant="body" component="div" gutterBottom color="danger">
                {createAdminUserFailureMessage}
            </Typography>}
            <Box display="flex" justifyContent="center" alignItems="center" className={classes.rowWrapButtons}>
                <Box display="flex" justifyContent="center" alignItems="center">
                    <Button
                        className={classes.cancelButton}
                        variant="tertiary"
                        disabled={false}
                        onClick={handleClose}
                        test-id="cancel-filters"
                    >
                        {t("users.cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        className={classes.showResultsButton}
                        disabled={!emailValue || !passwordValue || (!!passwordValue && passwordValue.length < 8) || (passwordValue !== confirmPasswordValue) || (!!emailValue && !validateEmail(emailValue))}
                        onClick={create}
                        test-id="show-results"
                    >
                        {t("users.createNewUser")}
                    </Button>
                </Box>
            </Box>
        </Modal>
    )
}
export default AddNewUserModal;